/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Suspense } from 'react'
import { createBrowserRouter, Location, useParams } from 'react-router-dom'
import UserLayout from 'src/layouts/UserLayout'
import { ReactRouterRedux } from 'src/components/ReactRouterRedux'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import {
  usePermission,
  PermissionPath,
  useSchemaPermissions,
} from 'src/hooks/usePermission'

const pagesCache = new Map<string, React.ComponentType<any>>()

const load = (props: { page: string; permission?: PermissionPath }) => () => {
  const { permission } = props
  const isAccess = usePermission(permission)
  const page = isAccess ? props.page : 'access-denied'

  const PageCache = pagesCache.get(page)

  if (PageCache) {
    return <PageCache />
  } else {
    const Page = React.lazy(() =>
      import(`src/pages/${page}`).then((pageModule) => {
        pagesCache.set(page, pageModule.default)

        return pageModule
      })
    )

    return (
      <Suspense
        fallback={
          <Stack
            sx={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <Page />
      </Suspense>
    )
  }
}

const loadStoragePage =
  (props: { page: string; permission?: PermissionPath }) => () => {
    const { permission } = props
    const isAccess = usePermission(permission)
    const { schemeName = '' } = useParams<{ schemeName: string }>()
    const schemaPermissions = useSchemaPermissions(schemeName)

    const page =
      isAccess && schemaPermissions.show ? props.page : 'access-denied'

    let PageCache = pagesCache.get(page)

    if (!PageCache) {
      PageCache = React.lazy(() =>
        import(`src/pages/${page}`).then((pageModule) => {
          return pageModule
        })
      )

      pagesCache.set(page, PageCache)
    }

    return (
      <Suspense
        fallback={
          <Stack
            sx={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <PageCache />
      </Suspense>
    )
  }
export const router = createBrowserRouter([
  {
    element: (
      <ReactRouterRedux>
        <UserLayout contentHeightFixed />
      </ReactRouterRedux>
    ),
    children: [
      {
        path: '',
        Component: load({
          page: 'dashboard',
          permission: undefined,
        }),
      },

      {
        path: 'callHistory',
        Component: load({
          page: 'callHistory',
          permission: undefined,
        }),
      },
      {
        path: 'marketing',
        Component: load({
          page: 'marketing',
          permission: undefined,
        }),
      },
      {
        path: 'profile',
        Component: load({
          page: 'profile',
        }),
      },

      {
        path: 'settings',
        Component: load({
          page: 'settings',
        }),
      },

      {
        path: 'storage/:schemeName',
        Component: load({
          page: 'objectContainer',
        }),
        children: [
          {
            path: '',
            Component: loadStoragePage({
              page: 'storage/object',
            }),
          },
          {
            path: ':objectId',
            Component: loadStoragePage({
              page: 'storage/object/id',
            }),
          },
          {
            path: ':objectId/details',
            Component: loadStoragePage({
              page: 'storage/object/id/details',
            }),
            children: [
              {
                path: '',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/dashboard',
                }),
              },
              {
                path: 'dashboard',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/dashboard',
                }),
              },
              {
                path: 'trends',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/trends',
                }),
              },
              {
                path: 'events',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/events',
                }),
              },
              {
                path: 'elements',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/elements',
                }),
              },
              {
                path: 'contacts',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/contacts',
                }),
              },
              {
                path: 'nps',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/nps',
                }),
              },
              {
                path: 'settings',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/settings',
                }),
              },
              {
                path: 'touch-points',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/touch-points',
                }),
              },
              {
                path: 'tasks',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/tasks',
                }),
              },
              {
                path: 'notes',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/notes',
                }),
              },
              {
                path: 'conversations',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/conversations',
                }),
              },
              {
                path: 'emails',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/emails',
                }),
              },
              {
                path: 'attachments',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/attachments',
                }),
              },
              {
                path: 'play-books',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/play-books',
                }),
              },
            ],
          },
        ],
      },

      // {
      //   path: 'tasks',
      //   Component: load({
      //     page: 'tasks',
      //     permission: 'tasks.index',
      //   }),
      // },
      // {
      //   path: 'health-score',
      //   Component: load({
      //     page: 'healthScore',
      //     permission: undefined,
      //   }),
      // },

      {
        path: 'email',
        Component: load({
          page: 'email',
          // permission: 'mail.thred.index',
        }),
      },
      // {
      //   path: 'playbooks',
      //   Component: load({
      //     page: 'playbooks',
      // permission: 'playbooks.index',
      //   }),
      // },
      {
        path: 'templates',
        children: [
          {
            path: ':type',
            Component: load({
              page: 'templates',
              // permission: 'templates.index',
            }),
          },
        ],
      },
      {
        path: 'numbers',
        Component: load({
          page: 'admin/numbers',
          // permission: 'communication.twilio.index',
        }), // need transfer to folder load/numbers
      },
      {
        path: 'billing-history',
        Component: load({
          page: 'billing-history',
          permission: 'auth.users',
        }),
      },
      {
        path: 'tg-accounts',
        Component: load({
          page: 'admin/tgNumbers',
          // permission: 'communication.telegram.index',
        }), // need transfer to folder load/tgNumbers
      },
      {
        path: 'forms',
        Component: load({
          page: 'forms',
          // permission: 'auth.users',
        }),
      },
      {
        path: 'admin',
        children: [
          {
            path: 'fields/:schemeName',
            Component: load({
              page: 'admin/fields/schemeName',
              permission: 'storage.objectScheme',
            }),
          },

          {
            path: 'groups/:schemeName',
            Component: load({
              page: 'admin/groups/schemeName',
              permission: 'storage.objectScheme',
            }),
          },

          {
            path: 'users',
            Component: load({
              page: 'admin/users',
              permission: 'auth.users',
            }),
          },
          {
            path: 'integration',
            Component: load({
              page: 'admin/integration',
              // permission: 'auth.users.index',
            }),
          },
          {
            path: 'users-groups',
            Component: load({
              page: 'admin/users-groups',
              permission: 'auth.groups',
            }),
          },
          {
            path: 'api-keys',
            Component: load({
              page: 'admin/api-keys',
              permission: 'auth.users',
            }),
          },
          {
            path: 'schemes',
            Component: load({
              page: 'admin/schemes',
              permission: 'storage.objectScheme',
            }),
          },
        ],
      },
    ],
  },
  {
    element: <UserLayout />,
    children: [
      {
        path: '*',
        Component: load({
          page: '404',
          permission: undefined,
        }),
      },
    ],
  },
  {
    path: 'login',
    Component: load({
      page: 'login',
      permission: undefined,
    }),
  },
])

export interface RouterHistory {
  locations: Location[]
  getPrevLocation: () => Location | null
}
export const routerHistory: RouterHistory = {
  locations: [],
  getPrevLocation: () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [current, prev] = routerHistory.locations

    return prev
  },
}

router.subscribe(({ location }) => {
  const [prev] = routerHistory.locations

  const isNotEqual =
    prev &&
    // prev.hash !== location.hash ||
    prev.pathname !== location.pathname
  // prev.search !== location.search ||
  // prev.state !== location.state

  if (prev && isNotEqual) {
    routerHistory.locations = [{ ...location }, ...routerHistory.locations]
  }
  if (!prev) {
    routerHistory.locations = [{ ...location }]
  }
  if (routerHistory.locations.length > 5) {
    routerHistory.locations = routerHistory.locations.slice(0, 5)
  }
})

export default router
